<template>
  <UModal
    title="Хотите записаться на занятие?"
    description="Напишите нам и мы свяжемся с вами в ближайшее время"
    :ui="{
      content: 'max-w-135',
      title: 'text-size-h3! font-semibold text-modal-title max-w-4/6',
      close: 'border border-neutral-border-default w-8 h-8',
    }"
  >
    <template #body>
      <UForm
        ref="formRef"
        :schema="schema"
        :state="form"
        class="space-y-3"
        @submit="submit"
      >
        <UFormField name="visitor_name">
          <UInput
            v-model="form.visitor_name"
            class="w-full"
            placeholder="Ваше имя *"
          />
        </UFormField>

        <UFormField name="phone">
          <UInput
            v-model="form.phone"
            v-maska="'+7 (###) ###-##-##'"
            class="w-full"
            type="phone"
            placeholder="Телефон *"
          />
        </UFormField>

        <UFormField name="email">
          <UInput
            v-model="form.email"
            class="w-full"
            type="email"
            placeholder="Ваша почта *"
          />
        </UFormField>

        <UFormField name="message">
          <UTextarea
            v-model="form.message"
            class="w-full"
            placeholder="Оставьте сообщение *"
          />
        </UFormField>

        <UFormField name="agree">
          <UCheckbox
            v-model="form.agree"
            required
            :ui="{
              label: 'font-lato! text-xs text-content-default',
            }"
          >
            <template #label>
              Я ознакомлен(-а) с <a
                href="/documents/personal-data"
                target="_blank"
                rel="noopener noreferrer"
                class="text-link"
              >
                Политикой обработки персональных данных
              </a>
              и даю своё
              <a
                href="/documents/personal-data"
                target="_blank"
                rel="noopener noreferrer"
                class="text-link"
              >
                согласие на обработку персональных данных</a
              >.
            </template>
          </UCheckbox>
        </UFormField>

        <UButton
          class="w-full justify-center mt-3"
          size="xl"
          @click="formRef?.submit()"
        >
          Отправить
        </UButton>
      </UForm>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import type { FormSubmitEvent } from '@nuxt/ui'
import { useDebounceFn } from '@vueuse/core'
import { vMaska } from 'maska/vue'
import * as v from 'valibot'
import type { StoreTrainingSignupRequest } from '~/api'
import { RUSSIAN_PHONE_REGEX } from '~/shared'
import { useSuccessModal } from '~/shared/modals'

defineOptions({
  name: 'SignForClassModal',
})

const emit = defineEmits<{
  close: []
}>()

const props = defineProps<{
  id: string
  apiMethod?: (id: string, data: StoreTrainingSignupRequest) => Promise<void>
}>()

const toast = useToast()

const formRef = useTemplateRef('formRef')

const schema = v.object({
  visitor_name: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, 'Укажите ваше имя'),
  ),
  email: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, 'Укажите email'),
    v.email('Некорректный email'),
  ),
  phone: v.pipe(
    v.string('Номер телефона должен быть строкой'),
    v.trim(),
    v.regex(RUSSIAN_PHONE_REGEX, 'Неверный формат российского номера телефона'),
  ),
  message: v.pipe(
    v.string('Оставьте сообщение'),
    v.trim(),
    v.minLength(10, 'Минимальная длина сообщения – 10 символов'),
  ),
  agree: v.literal(
    true,
    'Пожалуйста, подтвердите согласие с указанными документами',
  ),
})

type FormType = v.InferOutput<typeof schema>

const getInitialForm = (): FormType => ({
  visitor_name: '',
  email: '',
  phone: '',
  message: '', // хак для валидации literal(true) при старте
  agree: false as unknown as true, // хак для валидации literal(true) при старте
})

const form = reactive(getInitialForm())

const resetForm = () => {
  Object.assign(form, getInitialForm())
  formRef.value?.clear()
}

const submit = useDebounceFn(async (event: FormSubmitEvent<FormType>) => {
  if (props.apiMethod) {
    try {
      await props.apiMethod(props.id as string, event.data)
      resetForm()

      emit('close')
      const { open } = useSuccessModal({
        description:
          'Заявка будет рассмотрена администратором в течение 3–5 рабочих дней',
      })
      open()
    } catch ({ error }: any) {
      toast.add({
        title: 'Произошла ошибка при отправке записи',
        description: error?.message,
        color: 'error',
        icon: 'i-local-cross',
      })
    }
  }
}, 1000)
</script>

<style lang="scss" scoped></style>
